/** Postal code helpers for supported debtor countries. */

import { coerceDebtorCountry, normalizeDebtorCountry } from './debtorCountry.js';

const US_ZIP = /^\d{5}(?:-?\d{4})?$/;
const CA_POSTAL = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z][ ]?\d[ABCEGHJ-NPRSTV-Z]\d$/i;
const GB_POSTAL = /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i;
const AU_POSTAL = /^\d{4}$/;
const NZ_POSTAL = /^\d{4}$/;
const IE_POSTAL = /^[A-Z]\d{2}\s?[A-Z0-9]{4}$/i;

export function normalizePostalCode(raw) {
  const value = String(raw || '').trim().toUpperCase();
  if (!value) return '';

  const compact = value.replace(/\s+/g, '');
  if (US_ZIP.test(compact) || US_ZIP.test(value)) {
    if (/^\d{9}$/.test(compact)) return `${compact.slice(0, 5)}-${compact.slice(5)}`;
    if (/^\d{5}-\d{4}$/.test(value)) return value;
    return compact.slice(0, 5);
  }

  if (CA_POSTAL.test(compact) || CA_POSTAL.test(value)) {
    const body = compact.replace(/[^A-Z0-9]/gi, '');
    if (body.length === 6) return `${body.slice(0, 3)} ${body.slice(3)}`;
  }

  if (GB_POSTAL.test(value) || GB_POSTAL.test(compact)) {
    const body = compact.replace(/[^A-Z0-9]/gi, '');
    if (body.length >= 5) {
      return `${body.slice(0, body.length - 3)} ${body.slice(body.length - 3)}`;
    }
  }

  if (IE_POSTAL.test(value) || IE_POSTAL.test(compact)) {
    const body = compact.replace(/[^A-Z0-9]/gi, '');
    if (body.length === 7) return `${body.slice(0, 3)} ${body.slice(3)}`;
  }

  return value;
}

export function isValidUsPostalCode(raw) {
  const normalized = normalizePostalCode(raw);
  if (!normalized) return true;
  const compact = normalized.replace(/\s+/g, '');
  return US_ZIP.test(normalized) || US_ZIP.test(compact);
}

export function isValidCaPostalCode(raw) {
  const normalized = normalizePostalCode(raw);
  if (!normalized) return true;
  const compact = normalized.replace(/\s+/g, '');
  return CA_POSTAL.test(normalized) || CA_POSTAL.test(compact);
}

export function isValidGbPostalCode(raw) {
  const normalized = normalizePostalCode(raw);
  if (!normalized) return true;
  const compact = normalized.replace(/\s+/g, '');
  return GB_POSTAL.test(normalized) || GB_POSTAL.test(compact);
}

export function isValidAuPostalCode(raw) {
  const normalized = normalizePostalCode(raw);
  if (!normalized) return true;
  return AU_POSTAL.test(normalized.replace(/\s+/g, ''));
}

export function isValidNzPostalCode(raw) {
  const normalized = normalizePostalCode(raw);
  if (!normalized) return true;
  return NZ_POSTAL.test(normalized.replace(/\s+/g, ''));
}

export function isValidIePostalCode(raw) {
  const normalized = normalizePostalCode(raw);
  if (!normalized) return true;
  const compact = normalized.replace(/\s+/g, '');
  return IE_POSTAL.test(normalized) || IE_POSTAL.test(compact);
}

export function isValidUsOrCaPostalCode(raw) {
  return isValidUsPostalCode(raw) || isValidCaPostalCode(raw);
}

export function isValidPostalForCountry(raw, country) {
  const code = coerceDebtorCountry(country) || normalizeDebtorCountry(country);
  if (!String(raw || '').trim()) return true;
  switch (code) {
    case 'CA':
      return isValidCaPostalCode(raw);
    case 'GB':
      return isValidGbPostalCode(raw);
    case 'AU':
      return isValidAuPostalCode(raw);
    case 'NZ':
      return isValidNzPostalCode(raw);
    case 'IE':
      return isValidIePostalCode(raw);
    case 'US':
      return isValidUsPostalCode(raw);
    default:
      return (
        isValidUsOrCaPostalCode(raw) ||
        isValidGbPostalCode(raw) ||
        isValidAuPostalCode(raw) ||
        isValidNzPostalCode(raw) ||
        isValidIePostalCode(raw)
      );
  }
}

export function postalCodeErrorMessage(raw, country) {
  if (isValidPostalForCountry(raw, country)) return '';
  const code = coerceDebtorCountry(country) || normalizeDebtorCountry(country);
  switch (code) {
    case 'CA':
      return 'Enter a valid Canadian postal code (A1A 1A1).';
    case 'GB':
      return 'Enter a valid UK postcode (SW1A 1AA).';
    case 'AU':
      return 'Enter a valid Australian postcode (2000).';
    case 'NZ':
      return 'Enter a valid New Zealand postcode (6011).';
    case 'IE':
      return 'Enter a valid Irish Eircode (D02 AF30).';
    case 'US':
      return 'Enter a valid US ZIP (12345 or 12345-6789).';
    default:
      return 'Enter a valid postal code for the selected country.';
  }
}
